import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface DashboardRow {
  readonly dashboardId: string;
  readonly dashboardCode: string;
  readonly dashboardName: string;
  readonly dashboardType: string;
  readonly visibility: string;
  readonly status: string;
  readonly isRealtime: boolean;
  readonly refreshIntervalSecs: number;
}

interface RenderedDashboard {
  readonly dashboard: DashboardRow;
  readonly widgets: ReadonlyArray<Record<string, unknown>>;
  readonly generatedAt: string;
}

const TYPES = ['', 'EXECUTIVE', 'OPERATIONAL', 'DEPARTMENT', 'REAL_TIME', 'ANALYTICAL', 'COMPLIANCE', 'RISK'];
const STATUSES = ['', 'DRAFT', 'PUBLISHED', 'ARCHIVED'];
const VISIBILITIES = ['', 'PRIVATE', 'SHARED', 'PUBLIC'];

/**
 * M37 — Operational Dashboard (§28). Register of the tenant's analytics
 * dashboards (T494) across the 7 dashboard types (executive → risk), showing
 * type, sharing visibility (BR-02), realtime flag and publication status.
 * A dashboard can be rendered (server assembles its widgets from live KPI /
 * dataset values — DASHBOARD_VIEW) and a DRAFT can be published
 * (DASHBOARD_PUBLISH). Filterable by type, status and visibility. Tenant
 * isolation is enforced server-side (LAW 4).
 */
export function OperationalDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [total, setTotal] = useState(0);
  const [dashboardType, setDashboardType] = useState('');
  const [status, setStatus] = useState('');
  const [visibility, setVisibility] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [rendered, setRendered] = useState<RenderedDashboard | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (dashboardType) params.set('dashboardType', dashboardType);
    if (status) params.set('status', status);
    if (visibility) params.set('visibility', visibility);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: DashboardRow[]; total: number }>(`/v1/bi/dashboards${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, dashboardType, status, visibility, q, reload]);

  const render = async (id: string): Promise<void> => {
    setActionError(null);
    setRendered(null);
    setBusyId(id);
    try {
      const r = await apiRequest<{ data: RenderedDashboard }>(`/v1/bi/dashboards/${id}/render`, { token });
      setRendered(r.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.operational.notAuthView'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  const publish = async (id: string): Promise<void> => {
    setActionError(null);
    setBusyId(id);
    try {
      await apiRequest<{ data: DashboardRow }>(`/v1/bi/dashboards/${id}/publish`, { method: 'POST', token });
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.operational.notAuthPublish'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="operational-dashboard">
      <h1>{t('m37.operational.title')}</h1>
      <div className="filters">
        <label>
          {t('m37.common.type')}
          <select aria-label="filter-type" value={dashboardType} onChange={(e) => setDashboardType(e.target.value)}>
            {TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m37.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m37.operational.visibility')}
          <select aria-label="filter-visibility" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
            {VISIBILITIES.map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m37.common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m37.common.searchPlaceholder')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {actionError && <p role="alert" aria-label="action-error">{actionError}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="dashboard-count">{t('m37.operational.count', { count: total })}</p>
          <DataTable
            ariaLabel="dashboard-table"
            rows={rows}
            rowKey={(r) => r.dashboardId}
            columns={[
              { key: 'dashboardCode', header: t('m37.common.code'), render: (r) => r.dashboardCode },
              { key: 'dashboardName', header: t('m37.common.name'), render: (r) => r.dashboardName },
              { key: 'dashboardType', header: t('m37.common.type'), render: (r) => r.dashboardType },
              { key: 'visibility', header: t('m37.operational.visibility'), render: (r) => r.visibility },
              { key: 'isRealtime', header: t('m37.operational.realtime'), render: (r) => (r.isRealtime ? t('common.yes') : t('common.no')) },
              { key: 'status', header: t('m37.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m37.common.actions'), render: (r) => (
                  <>
                    <Button variant="secondary" type="button" aria-label={`render-${r.dashboardId}`} disabled={busyId === r.dashboardId} onClick={() => void render(r.dashboardId)}>{t('m37.operational.render')}</Button>
                    {r.status === 'DRAFT' && (
                      <Button variant="primary" type="button" aria-label={`publish-${r.dashboardId}`} disabled={busyId === r.dashboardId} onClick={() => void publish(r.dashboardId)}>{t('m37.operational.publish')}</Button>
                    )}
                  </>
              ) },
            ]}
          />

          {rendered && (
            <section aria-label="rendered-dashboard">
              <h2>{rendered.dashboard.dashboardName}</h2>
              <p aria-label="rendered-meta">
                {t('m37.operational.renderedMeta', { count: rendered.widgets.length, when: at(rendered.generatedAt) })}
              </p>
              <table aria-label="widget-table">
                <thead><tr><th>#</th><th>{t('m37.operational.colWidget')}</th></tr></thead>
                <tbody>
                  {rendered.widgets.map((w, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td aria-label={`widget-${i}`}>{JSON.stringify(w)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </main>
  );
}
